import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bot,
  CheckSquare,
  Square,
  Loader2,
  Mail,
  Phone,
  Calendar,
  MapPin,
  MessageSquare,
  X,
  Copy,
  ExternalLink,
  Video,
  Trash2,
} from 'lucide-react';
import {
  vendorExternalLeadApi,
  useUpdateExternalLead,
  useUpdateExternalLeadChecklist,
  useUpdateExternalLeadDeal,
  useUpdateExternalLeadStatus,
  useConvertExternalLead,
  useDeleteExternalLead,
  useSendExternalLeadMeetingEmail,
} from '../../../services/vendorExternalLeadService';
import LeadWorkspaceLinks from './LeadWorkspaceLinks';
import AskAgentModal from '../workspace/AskAgentModal';
import ChannelBadge from '../../shared/ChannelBadge';
import Input from '../../shared/Input';
import Button from '../../shared/Button';
import DatePicker from '../../client/DatePicker';
import TimePicker from '../../client/TimePicker';
import { useToastContext } from '../../../contexts/ToastContext';
import { formatDate } from '../../../utils/dateUtils';
import { useVendorSubscriptionStatus } from '../../../services/subscriptionService';
import { useVendorIntegrations } from '../../../services/vendorIntegrationsService';
import { canSendVendorEmail, vendorEmailLimitMessage } from '../../../utils/invoiceFormat';

const tagsToInput = (raw) => {
  if (Array.isArray(raw)) return raw.join(', ');
  if (typeof raw === 'string') return raw;
  return '';
};

const normalizeMeetingLink = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, '')}`;
};

/** Normalize API time strings (HH:mm, HH:mm:ss, h:mm AM/PM) → HH:mm for TimePicker. */
const toTimePickerValue = (raw) => {
  if (raw == null || raw === '') return '';
  const trimmed = String(raw).trim();
  const match12 = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
  if (match12) {
    let hours = Number(match12[1]);
    const minutes = Number(match12[2]);
    const period = match12[3].toUpperCase();
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return '';
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
  const match24 = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match24) {
    const hours = Number(match24[1]);
    const minutes = Number(match24[2]);
    if (Number.isNaN(hours) || Number.isNaN(minutes) || hours > 23 || minutes > 59) {
      return '';
    }
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }
  return '';
};

/**
 * External lead detail drawer: lean checklist, meeting link share, convert.
 */
const ExternalLeadDetailPanel = ({ leadId, onClose, onConverted }) => {
  const { success, error: showError } = useToastContext();
  const [notes, setNotes] = useState('');
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertDate, setConvertDate] = useState('');
  const [convertAmount, setConvertAmount] = useState('');
  const [eventDateDraft, setEventDateDraft] = useState('');
  const [localChecklist, setLocalChecklist] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [meetLink, setMeetLink] = useState('');
  const [tagsDraft, setTagsDraft] = useState('');
  const [showAskAgent, setShowAskAgent] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['vendorExternalLead', leadId],
    queryFn: () => vendorExternalLeadApi.getLead(leadId),
    enabled: Boolean(leadId),
    staleTime: 8_000,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });

  const lead = data?.data;
  const updateLead = useUpdateExternalLead();
  const updateChecklist = useUpdateExternalLeadChecklist();
  const updateDeal = useUpdateExternalLeadDeal();
  const updateStatus = useUpdateExternalLeadStatus();
  const convertLead = useConvertExternalLead();
  const deleteLead = useDeleteExternalLead();
  const sendMeetingEmail = useSendExternalLeadMeetingEmail();
  const { data: vendorSubPayload } = useVendorSubscriptionStatus();
  const vendorSubRaw = vendorSubPayload?.data ?? vendorSubPayload;
  const canEmailMeeting = canSendVendorEmail(vendorSubRaw);
  const { data: integRes } = useVendorIntegrations({ enabled: Boolean(leadId) });
  const canAgents = Boolean(integRes?.data?.capabilities?.can_agents);

  useEffect(() => {
    if (lead) {
      setNotes(lead.vendor_notes || '');
      setConvertDate(lead.event_date ? String(lead.event_date).slice(0, 10) : '');
      setEventDateDraft(lead.event_date ? String(lead.event_date).slice(0, 10) : '');
      setScheduleDate(
        lead.scheduled_date
          ? String(lead.scheduled_date).slice(0, 10)
          : lead.event_date
            ? String(lead.event_date).slice(0, 10)
            : ''
      );
      // Client form saves preferred time as event_time; vendor meeting uses scheduled_time.
      setScheduleTime(
        toTimePickerValue(lead.scheduled_time || lead.event_time || '')
      );
      setMeetLink(lead.google_meet_link || '');
      setTagsDraft(tagsToInput(lead.deal_summary?.tags));
      setLocalChecklist(null);
    }
  }, [lead?.id, lead?.updated_at, lead?.scheduled_time, lead?.event_time]);

  useEffect(() => {
    if (!leadId) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [leadId, onClose]);

  if (!leadId) return null;

  const checklistItems = localChecklist || lead?.checklist_progress?.items || [];
  const labeledAnswers =
    lead?.meta?.questionnaire_labeled ||
    Object.entries(lead?.questionnaire_responses || {}).map(([key, value]) => ({
      key,
      label: key,
      value,
    }));

  const toggleChecklist = async (key, done) => {
    if (!lead || updateChecklist.isPending) return;
    if (key === 'meeting_link_sent' && !done && !meetLink && !lead.google_meet_link) {
      showError(null, 'Add a meeting link below first.');
      return;
    }
    const nextMap = {};
    (lead.status_meta?.checklist || checklistItems.map((i) => i.key)).forEach((k) => {
      const item = checklistItems.find((i) => i.key === k);
      nextMap[k] = k === key ? !done : Boolean(item?.done ?? lead.action_checklist?.[k]);
    });
    const items = (lead.status_meta?.checklist || Object.keys(nextMap)).map((k) => ({
      key: k,
      label:
        lead.status_meta?.checklist_labels?.[k] ||
        checklistItems.find((i) => i.key === k)?.label ||
        k,
      done: Boolean(nextMap[k]),
    }));
    setLocalChecklist(items);
    try {
      await updateChecklist.mutateAsync({
        id: lead.id,
        checklist: nextMap,
        google_meet_link: meetLink || undefined,
        scheduled_date: scheduleDate || undefined,
        scheduled_time: scheduleTime || undefined,
      });
      success(null, 'Updated.');
      refetch();
    } catch (e) {
      setLocalChecklist(null);
      showError(null, e?.response?.data?.message || 'Could not update checklist.');
    }
  };

  const saveNotes = async () => {
    if (!lead) return;
    try {
      await updateLead.mutateAsync({ id: lead.id, vendor_notes: notes });
      success(null, 'Notes saved.');
    } catch (e) {
      showError(null, e?.response?.data?.message || 'Failed to save notes.');
    }
  };

  const saveEventDate = async () => {
    if (!lead) return;
    try {
      await updateLead.mutateAsync({
        id: lead.id,
        event_date: eventDateDraft || null,
      });
      success(null, 'Event date saved.');
      refetch();
    } catch (e) {
      showError(null, e?.response?.data?.message || 'Failed to save event date.');
    }
  };

  const saveTags = async () => {
    if (!lead) return;
    try {
      await updateDeal.mutateAsync({ id: lead.id, tags: tagsDraft });
      success(null, 'Tags saved.');
      refetch();
    } catch (e) {
      showError(null, e?.response?.data?.message || 'Failed to save tags.');
    }
  };

  const saveMeeting = async () => {
    if (!lead) return;
    const link = normalizeMeetingLink(meetLink);
    if (meetLink.trim() && !link) {
      showError(null, 'Enter a valid meeting link.');
      return;
    }
    try {
      await updateLead.mutateAsync({
        id: lead.id,
        scheduled_date: scheduleDate || null,
        scheduled_time: scheduleTime || null,
        google_meet_link: link || null,
      });
      if (link) setMeetLink(link);
      success(null, link ? 'Meeting details saved.' : 'Schedule saved.');
      refetch();
    } catch (e) {
      showError(null, e?.response?.data?.message || 'Could not save meeting details.');
    }
  };

  const meetingAlreadySent = Boolean(lead?.meet_link_sent_at);

  const copyMeetingLink = async () => {
    const link = normalizeMeetingLink(meetLink || lead?.google_meet_link);
    if (!link) {
      showError(null, 'Add a meeting link first.');
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      success(
        null,
        meetingAlreadySent
          ? 'Copied — share from your own email'
          : 'Link copied.'
      );
    } catch {
      showError(null, 'Could not copy.');
    }
  };

  const forwardMeetingToClient = async () => {
    const link = normalizeMeetingLink(meetLink || lead?.google_meet_link);
    if (!link) {
      showError(null, 'Add a meeting link first.');
      return;
    }
    if (meetingAlreadySent) {
      await copyMeetingLink();
      return;
    }
    if (!lead.guest_email) {
      showError(null, 'This lead has no email address to forward to.');
      return;
    }
    if (!canEmailMeeting) {
      showError(null, vendorEmailLimitMessage(vendorSubRaw));
      return;
    }
    try {
      const res = await sendMeetingEmail.mutateAsync({
        id: lead.id,
        google_meet_link: link,
        scheduled_date: scheduleDate || undefined,
        scheduled_time: scheduleTime || undefined,
      });
      if (link) setMeetLink(link);
      if (res?.already_sent) {
        success(null, 'Already emailed — link copied for you to share externally.');
        try {
          await navigator.clipboard.writeText(link);
        } catch {
          /* ignore */
        }
      } else {
        success(null, 'Meeting link emailed to the guest.');
      }
      refetch();
    } catch (e) {
      showError(null, e?.response?.data?.message || 'Could not forward meeting email.');
    }
  };

  const moveStatus = async (status) => {
    if (!lead) return;
    const payload = { id: lead.id, status };
    if (status === 'meeting_scheduled') {
      payload.scheduled_date = scheduleDate || undefined;
      payload.scheduled_time = scheduleTime || undefined;
      payload.google_meet_link = normalizeMeetingLink(meetLink) || undefined;
    }
    try {
      await updateStatus.mutateAsync(payload);
      success(null, `Moved to ${status.replace(/_/g, ' ')}.`);
      refetch();
    } catch (e) {
      showError(null, e?.response?.data?.message || 'Status change not allowed.');
    }
  };

  const submitConvert = async () => {
    if (!lead || !convertDate) return;
    try {
      const res = await convertLead.mutateAsync({
        id: lead.id,
        event_date: convertDate,
        subtotal_amount: convertAmount === '' ? 0 : Number(convertAmount),
        custom_service_name: 'External inquiry',
      });
      success(null, 'Converted to a private booking.');
      setConvertOpen(false);
      refetch();
      const bookingId = res?.data?.booking?.id || res?.data?.converted_booking_id;
      onConverted?.(bookingId);
    } catch (e) {
      showError(null, e?.response?.data?.message || 'Conversion failed.');
    }
  };

  const allowedNext = lead?.status_meta?.allowed_next || [];
  const showMeetingBlock =
    lead &&
    ['pending', 'meeting_scheduled', 'meeting_done'].includes(lead.status);

  return (
    <>
    {createPortal(
    <div
      className="theme-vendalist fixed inset-0 z-[11000] flex items-stretch justify-end bg-transparent"
      role="dialog"
      aria-modal="true"
      aria-labelledby="external-lead-detail-title"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-black/40 backdrop-blur-[1px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative z-10 flex h-full min-h-0 w-full max-w-lg flex-col border-l border-border bg-surface shadow-xl">
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border bg-surface px-4 py-3 sm:px-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2
                id="external-lead-detail-title"
                className="truncate text-base font-semibold text-ink"
              >
                {lead?.guest_name || 'Lead'}
              </h2>
              <ChannelBadge channel="external" />
            </div>
            {lead?.status_meta?.label ? (
              <p className="mt-0.5 text-xs text-ink-tertiary">{lead.status_meta.label}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border p-1.5 text-ink-tertiary hover:bg-surface-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {isLoading || !lead ? (
          <div className="flex flex-1 items-center justify-center py-20">
            <Loader2 className="h-7 w-7 animate-spin text-ink" />
          </div>
        ) : (
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 sm:p-5">
            {lead.status_meta?.description ? (
              <p className="text-sm text-ink-secondary">{lead.status_meta.description}</p>
            ) : null}

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-secondary">
              {lead.guest_email ? (
                <span className="inline-flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5" />
                  {lead.guest_email}
                </span>
              ) : null}
              {lead.guest_phone ? (
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" />
                  {lead.guest_phone}
                </span>
              ) : null}
              {lead.event_date ? (
                <span className="inline-flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {formatDate(lead.event_date, 'short')}
                  {lead.event_time ? ` · ${lead.event_time}` : ''}
                </span>
              ) : null}
              {lead.event_location ? (
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {lead.event_location}
                </span>
              ) : null}
            </div>

            {lead.message ? (
              <p className="flex gap-1.5 text-sm text-ink-secondary">
                <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {lead.message}
              </p>
            ) : null}

            {labeledAnswers.length ? (
              <dl className="grid gap-1 rounded-md border border-border bg-surface-muted/40 px-3 py-2 text-xs sm:grid-cols-2">
                {labeledAnswers.map((row) => (
                  <div key={row.key}>
                    <dt className="font-medium text-ink-tertiary">{row.label}</dt>
                    <dd className="text-ink">{String(row.value ?? '')}</dd>
                  </div>
                ))}
              </dl>
            ) : null}

            {showMeetingBlock ? (
              <div className="space-y-3 rounded-lg border border-border p-3">
                <div className="flex items-center gap-2">
                  <Video className="h-4 w-4 text-ink-tertiary" />
                  <p className="text-sm font-semibold text-ink">Meeting</p>
                </div>
                <p className="text-xs text-ink-tertiary">
                  Paste your Zoom / Meet / Teams link and share it with the client.
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  <DatePicker
                    label="Meeting date"
                    value={scheduleDate}
                    onChange={(v) => setScheduleDate(v || '')}
                    size="sm"
                    allowPastDates
                    placeholder="Select date"
                  />
                  <TimePicker
                    label="Time"
                    value={scheduleTime}
                    onChange={(v) => setScheduleTime(v || '')}
                    size="sm"
                    format="12"
                    placeholder="Select time"
                  />
                </div>
                <Input
                  label="Meeting link"
                  placeholder="https://meet.google.com/…"
                  value={meetLink}
                  onChange={(e) => setMeetLink(e.target.value)}
                  size="sm"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    onClick={saveMeeting}
                    loading={updateLead.isPending}
                    className="!h-8 !text-xs"
                  >
                    Save meeting
                  </Button>
                  {meetingAlreadySent ? (
                    <span className="inline-flex h-8 items-center rounded-md border border-emerald-200 bg-emerald-50 px-2.5 text-[11px] font-medium text-emerald-800">
                      Link emailed
                    </span>
                  ) : canEmailMeeting ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={forwardMeetingToClient}
                      loading={sendMeetingEmail.isPending}
                      disabled={!lead.guest_email}
                      className="!h-8 !text-xs"
                      icon={Mail}
                    >
                      Email link once
                    </Button>
                  ) : (
                    <div className="inline-flex flex-col items-start gap-0.5">
                      <button
                        type="button"
                        disabled
                        title={vendorEmailLimitMessage(vendorSubRaw)}
                        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-surface-muted px-3 text-xs font-medium text-gray-400 cursor-not-allowed"
                      >
                        <Mail className="h-3.5 w-3.5" />
                        Email link once
                      </button>
                      <Link
                        to="/vendor/plan"
                        className="text-[11px] text-ink underline-offset-2 hover:underline"
                      >
                        Upgrade plan for more emails
                      </Link>
                    </div>
                  )}
                  {(meetLink || lead.google_meet_link) && (
                    <>
                      <button
                        type="button"
                        onClick={copyMeetingLink}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        {meetingAlreadySent ? 'Copy to share' : 'Copy'}
                      </button>
                      <a
                        href={normalizeMeetingLink(meetLink || lead.google_meet_link)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Open
                      </a>
                    </>
                  )}
                </div>
                {meetingAlreadySent ? (
                  <p className="text-[11px] text-ink-tertiary">
                    Platform email was sent once. Use Copy to share again from your own inbox.
                  </p>
                ) : null}
              </div>
            ) : null}

            <div className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-tertiary">
                  Event date
                </p>
                <span className="text-[11px] text-ink-tertiary">Used on invoice &amp; booking</span>
              </div>
              {lead.status === 'booked' || lead.converted_booking_id ? (
                <p className="text-sm text-ink">
                  {lead.event_date ? formatDate(lead.event_date, 'short') : 'Not set'}
                </p>
              ) : (
                <>
                  <DatePicker
                    label="Client event / job date"
                    value={eventDateDraft}
                    onChange={(v) => setEventDateDraft(v ? String(v).slice(0, 10) : '')}
                    size="sm"
                    allowPastDates={false}
                    placeholder="Select event date"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="!h-8 !text-xs"
                    loading={updateLead.isPending}
                    onClick={saveEventDate}
                  >
                    Save event date
                  </Button>
                </>
              )}
            </div>

            <LeadWorkspaceLinks lead={lead} channel="external" />

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                icon={Bot}
                className="!rounded-md"
                onClick={() => setShowAskAgent(true)}
              >
                Run agent
              </Button>
            </div>

            {checklistItems.length > 0 ? (
              <div>
                <p className="mb-1 text-sm font-semibold text-ink">Next step</p>
                <p className="mb-2 text-[11px] text-ink-tertiary">
                  One checkbox per stage — meeting link auto-checks when you share it.
                </p>
                <div className="space-y-2">
                  {checklistItems.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      disabled={updateChecklist.isPending}
                      onClick={() => toggleChecklist(item.key, item.done)}
                      className="flex w-full items-center gap-2.5 rounded-lg border border-border px-3 py-2 text-left text-sm hover:bg-surface-muted disabled:opacity-60"
                    >
                      {item.done ? (
                        <CheckSquare className="h-4 w-4 shrink-0 text-emerald-600" />
                      ) : (
                        <Square className="h-4 w-4 shrink-0 text-gray-400" />
                      )}
                      <span className={item.done ? 'text-ink-tertiary line-through' : 'text-ink'}>
                        {item.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Input
                label="Tags"
                value={tagsDraft}
                onChange={(e) => setTagsDraft(e.target.value)}
                onBlur={() => {
                  if (tagsDraft !== tagsToInput(lead.deal_summary?.tags)) saveTags();
                }}
                placeholder="e.g. vip, wedding, rush"
              />
              <p className="text-[11px] text-ink-tertiary">
                Used by Automations “If tag” filters.
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-[11px] font-medium uppercase tracking-wide text-ink-tertiary">
                Vendor notes
              </label>
              <textarea
                rows={3}
                className="w-full rounded-md border border-border bg-surface px-3 py-2 text-xs text-ink"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => {
                  if (notes !== (lead.vendor_notes || '')) saveNotes();
                }}
              />
            </div>

            {allowedNext.length > 0 ? (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-tertiary">
                  Move status
                </p>
                <div className="flex flex-wrap gap-2">
                  {allowedNext.map((status) => (
                    <button
                      key={status}
                      type="button"
                      onClick={() => moveStatus(status)}
                      disabled={updateStatus.isPending}
                      className="rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium capitalize hover:bg-surface-muted"
                    >
                      {status.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {lead.status !== 'booked' && !lead.converted_booking_id ? (
              <div className="space-y-3 rounded-lg border border-border p-3">
                {!convertOpen ? (
                  <Button
                    type="button"
                    onClick={() => setConvertOpen(true)}
                    className="!h-9 !w-full !text-xs"
                  >
                    Convert to private booking
                  </Button>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <DatePicker
                      label="Event date *"
                      value={convertDate}
                      onChange={(v) => setConvertDate(v || '')}
                      size="sm"
                      allowPastDates={false}
                      placeholder="Select date"
                      required
                    />
                    <Input
                      type="number"
                      label="Amount (optional)"
                      min="0"
                      step="0.01"
                      value={convertAmount}
                      onChange={(e) => setConvertAmount(e.target.value)}
                      size="sm"
                    />
                    <div className="flex flex-wrap gap-2 sm:col-span-2">
                      <Button
                        type="button"
                        onClick={submitConvert}
                        disabled={!convertDate || convertLead.isPending}
                        loading={convertLead.isPending}
                        className="!h-8 !text-xs"
                      >
                        Confirm convert
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setConvertOpen(false)}
                        className="!h-8 !text-xs"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {lead.status === 'booked' || lead.converted_booking_id ? (
              <div className="border-t border-border pt-4">
                <p className="rounded-md border border-border bg-surface-muted px-3 py-2 text-[11px] text-ink-tertiary">
                  Booked leads can’t be deleted. Open the linked booking, invoice, or contract from
                  Connected work above.
                </p>
              </div>
            ) : (
              <div className="border-t border-border pt-4">
                <Button
                  type="button"
                  variant="outline"
                  icon={Trash2}
                  className="!h-9 !w-full !border-red-200 !text-red-700 hover:!bg-red-50 !text-xs"
                  loading={deleteLead.isPending}
                  onClick={async () => {
                    if (
                      !window.confirm(
                        'Delete this lead? Linked bookings, invoices, and contracts are kept.'
                      )
                    ) {
                      return;
                    }
                    try {
                      await deleteLead.mutateAsync(lead.id);
                      success(null, 'Lead deleted.');
                      onClose?.();
                    } catch (e) {
                      showError(null, e?.response?.data?.message || 'Failed to delete lead.');
                    }
                  }}
                >
                  Delete lead
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  )}
    <AskAgentModal
      isOpen={showAskAgent}
      onClose={() => setShowAskAgent(false)}
      canAgents={canAgents}
      presetEntity={
        lead
          ? {
              type: 'external_lead',
              id: String(lead.id),
              label: lead.guest_name || 'External lead',
            }
          : null
      }
    />
    </>
  );
};

export default ExternalLeadDetailPanel;
